// flow
import {
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Tabs,
  Typography,
} from '@duxton-web/ui';
import React, { useMemo, useState } from 'react';

import SmartCampaignImageUpload from 'omega-rtc/components/form/ucd/SmartCampaignImageUpload';
import SmartCampaignPCIPreviewCard from 'omega-rtc/components/form/ucd/SmartCampaignPCIPreviewCard';
import SmartCampaignSendPreview from 'omega-rtc/components/form/ucd/SmartCampaignSendPreview';
import { urlPatternRule } from 'omega-rtc/constants/rules';
import {
  CREATE_SMART_CAMPAIGN_DEFAULTS,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_INBOX_CATEGORIES,
} from 'omega-rtc/constants/ucd';
import supportedLanguages from 'omega-rtc/data/languages.json';
import { useCreateSmartCampaign } from 'omega-rtc/components/container/CreateSmartCampaignProvider';
import { SendUniversal } from '@duxton-web/icons';

const SmartCampaignPCIMetadata = () => {
  const {
    updateImageData,
    selectedLanguages,
    imageDataForEachLanguage,
    primaryLanguage,
  } = useCreateSmartCampaign();
  const [activeTabKey, setActiveTabKey] = useState(primaryLanguage);
  const form = Form.useFormInstance();

  const tabItems = useMemo(() => {
    return selectedLanguages.map((languageCode) => ({
      key: supportedLanguages[languageCode].code,
      label: supportedLanguages[languageCode].label,
      forceRender: true,
      children: (
        <PCIInputForm
          language={languageCode}
          imageData={imageDataForEachLanguage[languageCode]}
          handleImageDataChange={(imageData) => {
            form.setFieldValue(
              [SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL, languageCode],
              (imageData && imageData.url) || '',
            );
            updateImageData({ imageData, language: languageCode });
          }}
        />
      ),
    }));
  }, [imageDataForEachLanguage, selectedLanguages]);
  const [sendPreviewModalOpen, setSendPreviewModalOpen] = useState(false);

  return (
    <Flex
      vertical
      gap={24}
      style={{
        width: 931,
        height: '100%',
        paddingBottom: 30,
        margin: '30px auto auto',
      }}
    >
      <Card
        title={<Typography.Title level={4}>Inbox message</Typography.Title>}
        extra={
          <Button
            size="small"
            icon={<SendUniversal />}
            onClick={() => setSendPreviewModalOpen(true)}
          >
            Send preview message
          </Button>
        }
      >
        <Flex gap={24}>
          <SmartCampaignPCIPreviewCard
            language={activeTabKey}
            imageURL={
              imageDataForEachLanguage[activeTabKey] &&
              imageDataForEachLanguage[activeTabKey].url
            }
            handleRemoveImage={() => {
              updateImageData({ undefined, language: activeTabKey });
            }}
          />
          <Flex
            vertical
            style={{
              width: '56%',
              height: '100%',
            }}
          >
            <Typography.Title
              style={{ fontSize: '18px', margin: 0, paddingBottom: '16px' }}
              level={4}
            >
              Context
            </Typography.Title>
            <Form.Item
              initialValue={CREATE_SMART_CAMPAIGN_DEFAULTS.inboxCategory}
              label="Category"
              required
              name={SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY}
              rules={[{ message: 'Please select a category', required: true }]}
            >
              <Select
                placeholder="Select Category"
                options={SMART_CAMPAIGN_INBOX_CATEGORIES}
              />
            </Form.Item>
            <Divider style={{ marginTop: 0 }} />
            <Typography.Title
              style={{ fontSize: '18px', margin: 0, paddingBottom: '16px' }}
              level={4}
            >
              Content
            </Typography.Title>
            <Tabs
              activeKey={activeTabKey}
              type="pill"
              onTabClick={(activeKey) => setActiveTabKey(activeKey)}
              items={tabItems}
            />
          </Flex>
        </Flex>
      </Card>
      <SmartCampaignSendPreview
        renderSelectedPrimaryLanguageVariants
        open={sendPreviewModalOpen}
        onClose={() => setSendPreviewModalOpen(false)}
        language={primaryLanguage}
      />
    </Flex>
  );
};

const PCIInputForm = ({ language, imageData, handleImageDataChange }) => {
  return (
    <Flex vertical>
      <Form.Item
        label="Title"
        required
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE, language]}
        rules={[{ message: 'Please enter title', required: true }]}
      >
        <Input.TextArea rows={2} placeholder="Enter title" />
      </Form.Item>
      <Form.Item
        label="Subtitle"
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE, language]}
      >
        <Input.TextArea rows={2} placeholder="Enter subtitle" />
      </Form.Item>
      <Form.Item
        label="Message (HTML Support)"
        required
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE, language]}
        rules={[{ message: 'Please enter message', required: true }]}
      >
        <Input.TextArea rows={5} placeholder="Enter message" />
      </Form.Item>
      <Form.Item
        label="Cover image"
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE, language]}
      >
        <SmartCampaignImageUpload
          imageData={imageData}
          handleImageDataChange={handleImageDataChange}
        />
      </Form.Item>
      <Form.Item
        label="Button text"
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT, language]}
      >
        <Input placeholder="Enter button text" />
      </Form.Item>
      <Form.Item
        label="Button link"
        name={[SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK, language]}
        rules={[
          {
            message: 'Please enter a valid URL',
            validator: (_, value) => {
              if (!value || urlPatternRule().test(value)) {
                return Promise.resolve();
              }
              return Promise.reject();
            },
          },
        ]}
      >
        <Input placeholder="Enter button link" />
      </Form.Item>
    </Flex>
  );
};

export default SmartCampaignPCIMetadata;
